<template>
    <div style="margin-bottom: 30px">
        <a-space>
                <span style="padding: 10px; border-radius: 5px; background: #c4302e">
                    <FilePdfOutlined style="color: #fff;font-size: 16px"/>
                </span>
            <span>
                    {{data.title}}
                </span>
        </a-space>
    </div>
    <div class="audio-player-card" :style="cardStyle">
        <!-- 错误提示 -->
        <a-alert
                v-if="error"
                :message="error"
                type="error"
                show-icon
                closable
                :style="{ marginTop: marginSM + 'px' }"
        />

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-placeholder" :style="{ padding: paddingMD + 'px', textAlign: 'center' }">
            <a-spin />
        </div>

        <!-- 字幕列表 -->
        <div
                v-else
                class="subtitles-container"
                ref="subtitlesContainer"
                :style="{
        borderTopColor: borderColor,
      }"
        >
            <div
                    v-for="(item, index) in subtitles"
                    :key="index"
                    class="subtitle-line"
                    :class="{ active: isActive(item) }"
                    @click="goToSubtitle(item)"
                    :style="{
                      padding: `${paddingSM}px ${paddingMD}px`,
                      marginBottom: marginXXS + 'px',
                      borderRadius: borderRadius + 'px'
                    }"
            >
                <tempate v-if="item.text">
                    <span class="subtitle-text" :style="{ fontSize: fontSizeBase + 'px', color: textPrimary }">
                      {{ item.text }}
                    </span>
                </tempate>
            </div>
        </div>
    </div>
    <audio
            ref="audioRef"
            :src="audioSrc"
            @timeupdate="throttledTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @play="playing = true"
            @pause="playing = false"
            @ended="onEnded"
            @error="onError"
    ></audio>

    <!-- 主控制条 -->
    <div class="controls primary-controls" :style="{ gap: marginSM + 'px' }">
        <span class="time" :style="{ fontSize: fontSizeSM + 'px', color: textSecondary }">
        {{ formatTime(currentTime) }}
      </span>
        <a-slider
                class="progress-slider"
                :value="currentTime"
                :max="duration"
                :tooltip-visible="false"
                @change="onSeek"
        />
        <span class="time" :style="{ fontSize: fontSizeSM + 'px', color: textSecondary }">
        {{ formatTime(duration) }}
      </span>
    </div>

    <!-- 扩展控制条 -->
    <div class="controls extra-controls" :style="{ gap: marginSM + 'px', marginTop: marginSM + 'px' }" style="text-align: center;">
        <a-select
                v-model:value="playbackRate"
                :style="{ width: '100px' }"
                @change="onPlaybackRateChange"
        >
            <a-select-option value="0.5">0.5x</a-select-option>
            <a-select-option value="0.75">0.75x</a-select-option>
            <a-select-option value="1">1x</a-select-option>
            <a-select-option value="1.25">1.25x</a-select-option>
            <a-select-option value="1.5">1.5x</a-select-option>
            <a-select-option value="2">2x</a-select-option>
        </a-select>
        <a-button
                @click="toggleSubtitlesExpand"
                :type="subtitlesExpanded ? 'primary' : 'primary'"
        >
            上一段
        </a-button>
        <span
                class="paper-primary-text"
                style="cursor: pointer; font-size: 30px;"
                @click="togglePlay"
                :type="playing ? 'primary' : 'default'"
                shape="circle"
        >
            <span>
                <PauseCircleOutlined v-if="playing"/>
                <PlayCircleOutlined v-else/>
            </span>
        </span>
        <a-button
                @click="toggleSubtitlesExpand"
                :type="subtitlesExpanded ? 'primary' : 'default'"
        >
            下一段
        </a-button>
    </div>

</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted, watch, nextTick, defineProps} from 'vue'
    import {
        PlayCircleOutlined,
        PauseCircleOutlined,
        FilePdfOutlined
    } from '@ant-design/icons-vue'
    import { message } from 'ant-design-vue'


    // 定义 props（纯 JavaScript 版本）
    const props = defineProps({
        data: { type: Object, required: true },
        audioSrc: { type: String, required: true },
        subtitles: { type: Array, required: true }, // 格式: [{ start: 0, end: 5, text: '...' }]
        // 样式参数（可选）
        bgColor: { type: String, default: '#ffffff' },
        borderColor: { type: String, default: '#f0f0f0' },
        textPrimary: { type: String, default: '#000000e0' },
        textSecondary: { type: String, default: '#00000073' },
        textTertiary: { type: String, default: '#00000040' },
        primaryColor: { type: String, default: '#1677ff' },
        borderRadius: { type: Number, default: 8 },
        paddingLG: { type: Number, default: 24 },
        paddingMD: { type: Number, default: 16 },
        paddingSM: { type: Number, default: 12 },
        marginLG: { type: Number, default: 24 },
        marginMD: { type: Number, default: 16 },
        marginSM: { type: Number, default: 12 },
        marginXS: { type: Number, default: 8 },
        marginXXS: { type: Number, default: 4 },
        fontSizeBase: { type: Number, default: 14 },
        fontSizeSM: { type: Number, default: 12 },
        boxShadow: { type: String, default: '0 2px 8px rgba(0,0,0,0.15)' }
    })

    // 音频元素引用
    const audioRef = ref(null)
    const subtitlesContainer = ref(null)

    // 播放状态
    const currentTime = ref(0)
    const duration = ref(0)
    const playing = ref(false)
    const playbackRate = ref('1')
    const loading = ref(true)
    const error = ref(null)
    const subtitlesExpanded = ref(false)
    const autoScroll = ref(true)

    // 移动端检测
    const isMobile = ref(window.innerWidth <= 640)

    // 卡片样式
    const cardStyle = computed(() => ({
        backgroundColor: props.bgColor,
        borderRadius: props.borderRadius + 'px',
        boxShadow: props.boxShadow,
        border: `1px solid ${props.borderColor}`,
        padding: props.paddingLG + 'px',
        marginBottom: props.marginLG + 'px',
        width: '100%',
        margin: '0 auto'
    }));

    // 工具函数：格式化时间
    const formatTime = (seconds) => {
        if (isNaN(seconds) || seconds === 0) return '00:00'
        const mins = Math.floor(seconds / 60)
        const secs = Math.floor(seconds % 60)
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }

    // 判断字幕是否激活
    const isActive = (subtitle) => {
        return currentTime.value >= subtitle.startTimeS && currentTime.value < subtitle.endTimeS
    }

    // 简单节流函数（避免引入 lodash）
    function throttle(func, delay) {
        let lastCall = 0
        return function (...args) {
            const now = Date.now()
            if (now - lastCall >= delay) {
                lastCall = now
                func.apply(this, args)
            }
        }
    }

    // 事件处理
    const onTimeUpdate = (e) => {
        const audio = e.target
        currentTime.value = audio.currentTime
    }

    const throttledTimeUpdate = throttle(onTimeUpdate, 100)

    const onLoadedMetadata = (e) => {
        const audio = e.target
        duration.value = audio.duration
        loading.value = false
    }

    const onEnded = () => {
        playing.value = false
        currentTime.value = 0
        if (audioRef.value) {
            audioRef.value.currentTime = 0
        }
    }

    const onError = (e) => {
        const audio = e.target
        error.value = `音频加载失败: ${audio.error?.message || '未知错误'}`
        loading.value = false
    }

    const togglePlay = () => {
        if (!audioRef.value) return
        if (playing.value) {
            audioRef.value.pause()
        } else {
            audioRef.value.play().catch(err => {
                message.error('播放失败：' + err.message)
            })
        }
    }

    const onSeek = (value) => {
        if (audioRef.value) {
            audioRef.value.currentTime = value
            currentTime.value = value
        }
    }

    // const onVolumeChange = (val) => {
    //     if (audioRef.value) {
    //         audioRef.value.volume = val
    //     }
    // }

    const onPlaybackRateChange = (val) => {
        if (audioRef.value) {
            audioRef.value.playbackRate = parseFloat(val)
        }
    }

    const goToSubtitle = (subtitle) => {
        let {startTimeS} = subtitle;
        if (audioRef.value) {
            audioRef.value.currentTime = startTimeS;
            currentTime.value = startTimeS;
            if (!playing.value) {
                audioRef.value.play()
            }
        }
    }

    const toggleSubtitlesExpand = () => {
        subtitlesExpanded.value = !subtitlesExpanded.value
    };

    // 监听激活字幕自动滚动
    watch(currentTime, () => {
        if (!autoScroll.value) return
        nextTick(() => {
            const activeElement = document.querySelector('.subtitle-line.active')
            if (activeElement && subtitlesContainer.value) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest'
                })
            }
        })
    })

    // 键盘事件：空格键暂停/播放
    const handleKeyDown = (e) => {
        if (e.code === 'Space' && !e.repeat) {
            e.preventDefault()
            togglePlay()
        }
    }

    onMounted(() => {
        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('resize', () => {
            isMobile.value = window.innerWidth <= 640
        })
    })

    onUnmounted(() => {
        window.removeEventListener('keydown', handleKeyDown)
    })
</script>

<style scoped>
    .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }
    .primary-controls {
        margin-top: 20px;
        margin-bottom: v-bind('marginSM + "px"');
    }
    .time {
        min-width: 45px;
        text-align: center;
    }
    .progress-slider {
        flex: 1;
        min-width: 120px;
        margin: 0 v-bind('marginXS + "px"');
    }
    .volume-slider {
        width: 80px;
    }
    .subtitles-container {
        max-height: 600px;
        overflow-y: auto;
        border-top-width: 1px;
        border-top-style: solid;
        padding-top: v-bind('paddingMD + "px"');
        scroll-behavior: smooth;
        transition: max-height 0.3s ease;
    }
    .subtitle-line {
        display: flex;
        align-items: baseline;
        cursor: pointer;
        transition: background-color 0.2s;
    }
    .subtitle-line:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
    .subtitle-line.active {
        background-color: rgba(22, 119, 255, 0.1);
        font-weight: 500;
        border-left: 3px solid v-bind('primaryColor');
    }
    .subtitle-time {
        min-width: 50px;
        margin-right: v-bind('marginMD + "px"');
        font-family: 'JetBrains Mono', monospace;
    }
    .subtitle-text {
        line-height: 1.6;
    }

    @media (max-width: 480px) {
        .subtitles-container {
            max-height: 500px;
        }
    }

    /* 隐藏滚动条 */
    ::-webkit-scrollbar {
        width: 0; /* 设置滚动条宽度 */
    }
</style>